<template>
  <header
    ref="header"
    class="refresh-nav"
    :class="{ 'refresh-nav--artist': isWorkspace }"
    @keydown.esc="closeMenus"
  >
    <div class="refresh-nav__top">
      <router-link
        :to="isWorkspace ? '/dashboard' : '/'"
        class="refresh-logo"
        aria-label="Indiefy"
      >
        <img
          :src="logo"
          alt="Indiefy"
          width="112"
          height="30"
        >
        <span
          v-if="isPremiumUser"
          class="refresh-pro-tag"
        >PRO</span>
      </router-link>
      <nav
        v-if="!isWorkspace"
        class="refresh-public-links"
        :aria-label="$t('refresh.navigation')"
      >
        <router-link to="/#grow">
          {{ $t('refresh.platform') }}
        </router-link>
        <router-link to="/#pricing">
          {{ $t('refresh.plans') }}
        </router-link>
        <router-link to="/artists-catalog">
          {{ $t('refresh.discover') }}
        </router-link>
      </nav>
      <div class="refresh-nav__actions tw:flex tw:items-center tw:gap-2">
        <NotificationInbox v-if="isWorkspace" />
        <button
          v-if="isPremiumUser"
          class="refresh-icon-button theme-control"
          :aria-label="$t('refresh.theme')"
          @click="themeStore.toggleDarkMode()"
        >
          <GrowthIcon name="moon" />
        </button>
        <div class="refresh-dropdown language-control">
          <button
            class="refresh-language"
            :aria-expanded="languageOpen"
            aria-controls="refresh-languages"
            :aria-label="$t('refresh.language')"
            @click="languageOpen = !languageOpen; accountOpen = false"
          >
            <GrowthIcon name="globe" />{{ locale.toUpperCase() }}
          </button>
          <div
            v-if="languageOpen"
            id="refresh-languages"
            class="refresh-menu"
          >
            <button
              v-for="language in languages"
              :key="language.code"
              :aria-pressed="locale === language.code"
              @click="setLanguage(language.code); closeMenus()"
            >
              {{ language.name }}
            </button>
          </div>
        </div>
        <template v-if="!isAuthenticated">
          <router-link
            to="/auth/login"
            class="refresh-login"
          >
            {{ $t('auth.sign-in') }}
          </router-link>
          <router-link
            to="/auth/signup"
            class="growth-button growth-button--dark"
          >
            {{ $t('refresh.start') }}<GrowthIcon name="up" />
          </router-link>
        </template>
        <div
          v-else
          class="refresh-dropdown"
        >
          <button
            class="refresh-avatar"
            :aria-label="$t('navigation.account')"
            :aria-expanded="accountOpen"
            aria-controls="refresh-account"
            @click="accountOpen = !accountOpen; languageOpen = false"
          >
            {{ initial }}
          </button>
          <div
            v-if="accountOpen"
            id="refresh-account"
            class="refresh-menu"
          >
            <p class="refresh-menu__name">
              {{ currentUser?.name || currentUser?.email }}
            </p>
            <router-link to="/account">
              {{ $t('navigation.account') }}
            </router-link>
            <router-link to="/artists">
              {{ $t('navigation.artists') }}
            </router-link>
            <router-link to="/form-submissions">
              {{ $t('formSubmissions.title') }}
            </router-link>
            <router-link to="/royalties">
              {{ $t('navigation.royalties') }}
            </router-link>
            <router-link to="/referral-program">
              {{ $t('navigation.referrals') }}
            </router-link>
            <a
              v-if="currentUser?.impersonate"
              href="https://indiefy.net/v1/auth/back-to-admin"
            >{{ $t('refresh.back-admin') }}</a>
            <button @click="logout">
              {{ $t('auth.sign-out') }}
            </button>
          </div>
        </div>
        <router-link
          v-if="isAuthenticated && !isWorkspace"
          to="/dashboard"
          class="growth-button growth-button--dark refresh-workspace-return"
        >
          {{ $t('navigation.dashboard') }}<GrowthIcon name="arrow" />
        </router-link>
        <button
          v-if="!isWorkspace"
          class="refresh-icon-button refresh-mobile-menu"
          :aria-expanded="publicOpen"
          aria-controls="refresh-public-menu"
          :aria-label="$t('refresh.navigation')"
          @click="publicOpen = !publicOpen"
        >
          <GrowthIcon :name="publicOpen ? 'close' : 'menu'" />
        </button>
      </div>
    </div>
    <nav
      v-if="!isWorkspace && publicOpen"
      id="refresh-public-menu"
      class="refresh-public-mobile"
      :aria-label="$t('refresh.navigation')"
    >
      <router-link to="/#grow">
        {{ $t('refresh.platform') }}
      </router-link>
      <router-link to="/#pricing">
        {{ $t('refresh.plans') }}
      </router-link>
      <router-link to="/artists-catalog">
        {{ $t('refresh.discover') }}
      </router-link>
      <router-link
        v-if="isAuthenticated"
        to="/dashboard"
      >
        {{ $t('navigation.dashboard') }}
      </router-link>
      <router-link
        v-else
        to="/auth/login"
      >
        {{ $t('auth.sign-in') }}
      </router-link>
    </nav>
    <aside
      v-if="isWorkspace"
      class="refresh-sidebar"
    >
      <p class="growth-eyebrow refresh-sidebar__caption">
        {{ $t('refresh.workspace') }}
      </p>
      <nav
        class="refresh-workspace-links"
        :aria-label="$t('refresh.navigation')"
      >
        <router-link
          v-for="item in navItems"
          :key="item.to"
          :to="item.to"
          :class="{ 'is-selected': route.path.startsWith(item.to) || (item.to === '/discover' && route.path === '/feed') }"
        >
          <GrowthIcon :name="item.icon" /><span>{{ $t(item.label) }}</span><span
            v-if="route.path.startsWith(item.to) || (item.to === '/discover' && route.path === '/feed')"
            class="refresh-nav-dot"
          />
        </router-link>
      </nav>
      <router-link
        to="/artists-catalog"
        class="refresh-community"
      >
        <GrowthIcon name="people" />{{ $t('refresh.community') }}<GrowthIcon name="up" />
      </router-link>
      <div
        v-if="!isPremiumUser"
        class="refresh-sidebar-pro"
      >
        <span class="refresh-pro-tag">INDIEFY PRO</span>
        <h2>{{ $t('refresh.pro-short') }}</h2>
        <p>{{ $t('refresh.pro-sidebar') }}</p>
        <router-link
          to="/upgrade?from=v4_growth_navigation"
          class="growth-button growth-button--lime"
        >
          {{ $t('refresh.explore-pro') }}<GrowthIcon name="up" />
        </router-link>
      </div>
      <div
        v-else
        class="refresh-sidebar-pro"
      >
        <GrowthIcon name="spark" /><p>{{ $t('refresh.pro-member') }}</p>
      </div>
    </aside>
  </header>
</template>
<script setup>
import { computed, ref, watch, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/store/auth';
import { useThemeStore } from '@/store/theme';
import { getAvailableLanguages, setLanguage } from '@/i18n';
import NotificationInbox from './NotificationInbox.vue';
import GrowthIcon from '@/components/growth/GrowthIcon.vue';
import logo from '@/assets/images/logo-dark.png';
const authStore = useAuthStore();
const themeStore = useThemeStore();
const route = useRoute();
const router = useRouter();
const { locale } = useI18n();
const header = ref(null);
const languageOpen = ref(false);
const accountOpen = ref(false);
const publicOpen = ref(false);
const languages = getAvailableLanguages();
const isAuthenticated = computed(() => authStore.isAuthenticated);
const isWorkspace = computed(() => isAuthenticated.value && route.name !== 'landing');
const currentUser = computed(() => authStore.currentUser);
const isPremiumUser = computed(() => !!currentUser.value?.subscription);
const initial = computed(() => (currentUser.value?.name || currentUser.value?.email || 'I').charAt(0).toUpperCase());
const navItems = [
  { to: '/dashboard', icon: 'home', label: 'refresh.home' },
  { to: '/distributions', icon: 'music', label: 'navigation.releases' },
  { to: '/analytics', icon: 'chart', label: 'navigation.analytics' },
  { to: '/tools', icon: 'spark', label: 'refresh.grow' },
  { to: '/discover', icon: 'globe', label: 'refresh.discover' },
];
const closeMenus = () => { languageOpen.value = false; accountOpen.value = false; publicOpen.value = false; };
const onOutside = event => { if (!header.value?.contains(event.target)) closeMenus(); };
const logout = async () => { closeMenus(); await authStore.logout(); router.push('/'); };
watch(() => route.fullPath, closeMenus);
onMounted(() => document.addEventListener('click', onOutside));
onUnmounted(() => document.removeEventListener('click', onOutside));
</script>
